function benchmark(fn, name, desc) {
  return function benchmarkedFunction(...args) {
    const startTime = performance.now();
    const result = fn(...args);
    const endTime = performance.now();
    console.log(name, desc, endTime - startTime);
    return result;
  };
}

function nextTick(fn) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(fn());
    }, 0);
  });
}

const groupByState = function groupByState(data) {
  return (data || []).reduce((acc, row) => {
    const [
      stateName,
      loanAmount,
      duration,
      score,
      numberOfPayments,
      APRmin,
      APRmax,
      paymentAmountmin,
      paymentAmountmax,
      disclosure,
      issuingOrganization,
      pricingPolicy,
      PaymentFreqency,
      ccStatus,
      loantype,
      typeloan,
      topDisclosure,
      bottomDisclosure,	
      rightDisclosure,
      amountFinanced,
      adminFees,
      latePaymentFee,
      returnedPaymentFee,
    ] = row;

    if (!acc[stateName]) {
      acc[stateName] = {};
    }
    if (!acc[stateName].items) {
      acc[stateName].items = [];
    }

    acc[stateName].items.push({
      loanAmount,
      duration,
      score,
      numberOfPayments,
      APRmin,
      APRmax,
      paymentAmountmin,
      paymentAmountmax,
      issuingOrganization,
      pricingPolicy,
      PaymentFreqency,
      ccStatus,
      typeloan,
      loantype,
      disclosure,
      topDisclosure,
      bottomDisclosure,	
      rightDisclosure,
      amountFinanced,
      adminFees,
      latePaymentFee,
      returnedPaymentFee,
    });

    return acc;
  }, {});
};

export function createApi(DATA_SOURCE = [], MAXMIN_DATA = []) {
  const indexes = {};
  
  async function withIndex(name, fn, defaultValue = []) {
    if (indexes[name]) {
      return indexes[name];
    }
    return nextTick(async () => {
      try {
        const data = await fn();
        indexes[name] = data;
        return data;
      } catch (err) {
        console.error('[API] withIndex error:', err);
        return defaultValue;
      }
    });
  }

  function toNum(str) {
    return +String(str).replaceAll(',', '');
  }

  function groupMaxMin() {
    return (MAXMIN_DATA || []).reduce((acc, row) => {
      const [state, uplMin = '', uplMax = '', splMin = '', splMax = ''] = row;

      const info = {
        hasSpl: true,
        upl: {
          min: toNum(uplMin),
          max: toNum(uplMax),
        },
        spl: {
          min: toNum(splMin),
          max: toNum(splMax),
        },
      };

      if (uplMin === splMin && uplMax === splMax) {
        info.hasSpl = false;
      }

      acc[state] = info;
      return acc;
    }, {});
  }

  async function getStates() {
    return withIndex('states', () => {
      const states = groupByState(DATA_SOURCE || []);
      
      Object.entries(states).forEach(([name, stateData]) => {
        const groupedMinMax = groupMaxMin();
        const meta = groupedMinMax[name] || {};
        
        const requireScore = stateData.items.some((row) =>
          row.score !== 'oportunupl' && row.score !== 'oportunspl' && row.score !== 'oportun');
        const hasSPL = stateData.items.some((row) =>
          row.typeloan.toLowerCase() === 'spl');
        
        meta.hasSPL = hasSPL;
        meta.requireScore = requireScore;

        const oldSpl = meta.hasSpl;
        delete meta.hasSpl;
        Object.defineProperty(meta, 'hasSpl', {
          get() {
            console.warn(
              `meta.hasSpl is deprecated. Use meta.hasSPL instead. hasSpl has a value of ${oldSpl}. hasSPL: ${meta.hasSPL}`,
            );
            return meta.hasSPL;
          },
        });

        stateData.meta = meta;
      });
      
      return states;
    }, {});
  }

  async function getStateNames() {
    const states = await getStates();
    return Object.keys(states);
  }

  async function getState(stateName) {
    if (!stateName) {
      console.warn('[API] getState called without stateName');
      return {};
    }
    const states = await getStates();
    
    if (!states[stateName]) {
      console.warn(`[API] State "${stateName}" not found in data`);
      return {};
    }
    
    return states[stateName];
  }

  async function getStateMetaData(stateName) {
    const stateData = await getState(stateName);
    return stateData.meta || {};
  }

  async function getCreditScoreOptions(stateName) {
    if (!stateName) {
      console.warn('[API] getCreditScoreOptions called without stateName');
      return [];
    }

    const scores = await withIndex(
      `${stateName}_creditScores`,
      async () => {
        const states = await getStates();
        const stateEntry = states[stateName];

        if (!stateEntry || !stateEntry.items) {
          throw new Error(`Invalid State Name: ${stateName}`);
        }

        const stateData = stateEntry.items;
        const scores = stateData.map((data) => data.score);

        return [...new Set(scores)];
      },
      [],
    );

    return scores;
  }

  async function getLoanAmountData({ state, score, type = 'upl' }) {
    if (!(state && score)) {
      console.warn('[API] getLoanAmountData requires state and score');
      return {};
    }

    const amounts = await withIndex(
      `${state}_${score}_${type}_amounts`,
      async () => {
        const states = await getStates();
        const stateEntry = states[state];

        if (!stateEntry || !stateEntry.items) {
          throw new Error(`Invalid State Name: ${state}`);
        }

        const stateData = stateEntry.items;
        const scoreData = stateData.filter(
          (data) => data.score === score && data.typeloan.toLowerCase() === type
        );

        const amountsData = scoreData.reduce((acc, data) => {
          const { duration, loanAmount } = data;
          if (!acc[loanAmount]) {
            acc[loanAmount] = {};
          }
          if (!acc[loanAmount][duration]) {
            acc[loanAmount][duration] = [];
          }

          acc[loanAmount][duration] = data;

          return acc;
        }, {});

        return amountsData;
      },
      {},
    );

    return amounts;
  }

  async function getLoanLengthData({ state, score, amount, type }) {
    return withIndex(
      `${state}_${score}_${amount}_lengths`,
      async () => {
        const loanAmountData = await getLoanAmountData({ state, score, type });

        const durations = loanAmountData[amount];
        if (!durations) {
          throw new Error(`Invalid loan amount: ${amount}`);
        }

        return durations;
      },
      {},
    );
  }

  async function getLoanLengths({ state, score, amount, type }) {
    if (!(state && score && amount)) {
      console.warn('[API] getLoanLengths requires state, score, and amount');
      return [];
    }

    const lengths = await getLoanLengthData({ state, score, amount, type });
    return Object.keys(lengths);
  }

  async function getLoanAmounts(params) {
    const loadAmounts = await getLoanAmountData(params);
    return Object.keys(loadAmounts);
  }

  async function getApr({ duration, amount, ...params }) {
    try {
      const loanAmounts = await getLoanAmountData({ amount, ...params });
      const amountData = loanAmounts[amount];
      if (!amountData) {
        throw new Error(`Invalid loan amount: ${amount}`);
      }

      const durData = amountData[duration];
      if (!durData) {
        return { min: '', max: '' };
      }

      const { APRmax, APRmin } = durData;
      return {
        min: APRmin,
        max: APRmax,
      };
    } catch (e) {
      console.error('[API] getApr error:', e);
      return { min: '', max: '' };
    }
  }

  async function getPayments({ duration, amount, ...params }) {
    try {
      const loanAmounts = await getLoanAmountData({ amount, ...params });

      const amountData = loanAmounts[amount];
      if (!amountData) {
        return { min: '', max: '' };
      }

      const durData = amountData[duration];
      if (!durData) {
        return { min: '', max: '' };
      }

      const { paymentAmountmax, paymentAmountmin } = durData;
      return {
        min: paymentAmountmin,
        max: paymentAmountmax,
      };
    } catch (e) {
      console.error('[API] getPayments error:', e);
      return { min: '', max: '' };
    }
  }

  async function getNoOfPayment({ duration, amount, ...params }) {
    try {
      const loanAmounts = await getLoanAmountData({ amount, ...params });

      const amountData = loanAmounts[amount];
      if (!amountData) {
        return '';
      }

      const lenData = amountData[duration];
      if (!lenData) {
        return '';
      }

      const { numberOfPayments } = lenData;
      return `${numberOfPayments}`;
    } catch (e) {
      console.error('[API] getNoOfPayment error:', e);
      return '';
    }
  }

  async function getDisclosure({ duration, amount, ...params }) {
    try {
      const loanAmounts = await getLoanAmountData({ amount, ...params });

      const amountData = loanAmounts[amount];
      if (!amountData) {
        throw new Error(`Invalid loan amount: ${amount}`);
      }

      const row = amountData[duration];
      if (!row) {
        return '';
      }

      const { disclosure } = row;
      return disclosure;
    } catch (e) {
      console.error('[API] getDisclosure error:', e);
      return '';
    }
  }

  async function getTableTopDisclosure({ duration, amount, ...params }) {
    try {
      const loanAmounts = await getLoanAmountData({ amount, ...params });

      const amountData = loanAmounts[amount];
      if (!amountData) {
        throw new Error(`Invalid loan amount: ${amount}`);
      }

      const row = amountData[duration];
      if (!row) {
        return '';
      }

      const { topDisclosure } = row;
      return topDisclosure;
    } catch (e) {
      console.error('[API] getTableTopDisclosure error:', e);
      return '';
    }
  }
  
  async function getTableBottomDisclosure({ duration, amount, ...params }) {
    try {
      const loanAmounts = await getLoanAmountData({ amount, ...params });

      const amountData = loanAmounts[amount];
      if (!amountData) {
        throw new Error(`Invalid loan amount: ${amount}`);
      }

      const row = amountData[duration];
      if (!row) {
        return '';
      }

      const { bottomDisclosure } = row;
      return bottomDisclosure;
    } catch (e) {
      console.error('[API] getTableBottomDisclosure error:', e);
      return '';
    }
  }
  
  async function getTableRightDisclosure({ duration, amount, ...params }) {
    try {
      const loanAmounts = await getLoanAmountData({ amount, ...params });

      const amountData = loanAmounts[amount];
      if (!amountData) {
        throw new Error(`Invalid loan amount: ${amount}`);
      }

      const row = amountData[duration];
      if (!row) {
        return '';
      }

      const { rightDisclosure } = row;
      return rightDisclosure;
    } catch (e) {
      console.error('[API] getTableRightDisclosure error:', e);
      return '';
    }
  }

  async function getTableAmountFinanced({ duration, amount, ...params }) {
    try {
      const loanAmounts = await getLoanAmountData({ amount, ...params });

      const amountData = loanAmounts[amount];
      if (!amountData) {
        throw new Error(`Invalid loan amount: ${amount}`);
      }

      const row = amountData[duration];
      if (!row) {
        return '';
      }

      const { amountFinanced } = row;
      return amountFinanced;
    } catch (e) {
      console.error('[API] getTableAmountFinanced error:', e);
      return '';
    }
  }

  async function getTableAdminFees({ duration, amount, ...params }) {
    try {
      const loanAmounts = await getLoanAmountData({ amount, ...params });

      const amountData = loanAmounts[amount];
      if (!amountData) {
        throw new Error(`Invalid loan amount: ${amount}`);
      }

      const row = amountData[duration];
      if (!row) {
        return '';
      }

      const { adminFees } = row;
      return adminFees;
    } catch (e) {
      console.error('[API] getTableAdminFees error:', e);
      return '';
    }
  }

  async function getTableLatePaymentFee({ duration, amount, ...params }) {
    try {
      const loanAmounts = await getLoanAmountData({ amount, ...params });

      const amountData = loanAmounts[amount];
      if (!amountData) {
        throw new Error(`Invalid loan amount: ${amount}`);
      }

      const row = amountData[duration];
      if (!row) {
        return '';
      }

      const { latePaymentFee } = row;
      return latePaymentFee;
    } catch (e) {
      console.error('[API] getTableLatePaymentFee error:', e);
      return '';
    }
  }

  async function getTableReturnedPaymentFee({ duration, amount, ...params }) {
    try {
      const loanAmounts = await getLoanAmountData({ amount, ...params });

      const amountData = loanAmounts[amount];
      if (!amountData) {
        throw new Error(`Invalid loan amount: ${amount}`);
      }

      const row = amountData[duration];
      if (!row) {
        return '';
      }

      const { returnedPaymentFee } = row;
      return returnedPaymentFee;
    } catch (e) {
      console.error('[API] getTableReturnedPaymentFee error:', e);
      return '';
    }
  }

  async function getLoanDetails({
    state, score, secure, amount, type, duration,
  }) {
    if (!state) {
      throw new Error('state is required');
    }

    const stateData = await getState(state);
    
    if (!stateData || !stateData.meta) {
      throw new Error(`No data available for state: ${state}`);
    }

    const { meta, items } = stateData;

    if (!items || items.length === 0) {
      throw new Error(`No loan data available for state: ${state}`);
    }

    const creditScores = await getCreditScoreOptions(state);

    let scoreName;
    let loanType;
    let secureType;

    if (meta.hasSPL) {
      secureType = secure;
      if (!meta.requireScore) {
        scoreName = 'oportunupl';
      }
    }

    if (meta.requireScore) {
      scoreName = score;
      if (secureType !== null) {
        if (secure === 'yes' && meta.hasSPL && type !== 'upl') {
          loanType = 'spl';
          scoreName = score;
        } else {
          loanType = 'upl';
          scoreName = score;
        }
      }
    } else if (secure === 'yes' && meta.hasSPL && type !== 'upl') {
      loanType = 'spl';
      scoreName = score;
    } else {
      loanType = 'upl';
      scoreName = score;
    }

    if (creditScores.length === 1) {
      scoreName = creditScores[0];
    }

    if (meta.requireScore) {
      if (!scoreName || !creditScores.includes(scoreName)) {
        throw new Error(`Credit score is required for ${state}`);
      }
    } else if (!scoreName) {
      scoreName = 'oportunupl';
    }

    if (meta.hasSPL && !secureType) {
      throw new Error(`Secure type is required for ${state}`);
    }

    const params = {
      state,
      amount: `${amount}`,
      duration,
      score: scoreName,
      type: loanType,
    };

    const amounts = await getLoanAmounts(params);
    
    if (!amount || !amounts.includes(amount)) {
      params.amount = amounts[Math.ceil(amounts.length / 2)] || amounts[0];
    }

    const durations = await getLoanLengths(params);

    if (!duration || !durations.includes(duration)) {
      params.duration = durations[0];
    }

    const payments = await getPayments(params);
    const noOfPayments = await getNoOfPayment(params);
    const apr = await getApr(params);
    const disclosure = await getDisclosure(params);
    const topDisclosure = await getTableTopDisclosure(params);
    const bottomDisclosure = await getTableBottomDisclosure(params);
    const rightDisclosure = await getTableRightDisclosure(params);
    const amountFinanced = await getTableAmountFinanced(params);
    const adminFees = await getTableAdminFees(params);
    const latePaymentFee = await getTableLatePaymentFee(params);
    const returnedPaymentFee = await getTableReturnedPaymentFee(params);

    const loanDetails = {
      meta,
      type: loanType,
      score: scoreName,
      secure,
      isSecure: secure === 'yes',
      amounts,
      amount: params.amount,
      durations,
      duration: params.duration,
      payments,
      noOfPayments,
      apr,
      disclosure,
      topDisclosure,
      bottomDisclosure,	
      rightDisclosure,
      amountFinanced,
      adminFees,
      latePaymentFee,
      returnedPaymentFee,
    };

    return loanDetails;
  }

  return {
    getStates,
    getStateMetaData,
    getStateNames,
    getLoanLengths,
    getLoanAmounts,
    getApr,
    getPayments,
    getNoOfPayment,
    getLoanDetails,
  };
}